import React, { useState, MouseEvent, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
  Box,
  Typography,
  ChipCustom,
  MaterialUICore,
  MaterialIcons,
  Grid,
} from '@iclinic/design-system';
import useStyles from './Suggestions.style';
import { getSuggestions, removeSuggestion } from '../state/suggestions';
import { suggestionsSelectors } from '../state/selectors';
import { addComplaint } from '../state';
import { ComplaintSuggestion } from '../types';
import { getIdFromTitle } from '../state/utils';

const { Menu, MenuItem, IconButton, useTheme } = MaterialUICore;

const { MoreVert, Close, Cancel } = MaterialIcons;

export default function Suggestions(): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteAction, setDeleteAction] = useState(false);
  const classes = useStyles();
  const iconColor = useTheme().palette.grey[500];

  const dispatch = useDispatch();
  const suggestions = useSelector(suggestionsSelectors.selectAll);

  useEffect(() => {
    dispatch(getSuggestions());
  }, [dispatch]);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCloseDelete = () => {
    setAnchorEl(null);
    setDeleteAction(true);
  };

  const renderSuggestion = useCallback(
    (suggestion: ComplaintSuggestion) => {
      const handleDelete = () => {
        dispatch(removeSuggestion(getIdFromTitle(suggestion)));
      };

      const handleClickSuggestion = () => {
        dispatch(
          addComplaint({
            title: suggestion.title,
            favorite: suggestion.favorite,
          }),
        );
      };

      let onDelete;
      let deleteIcon;
      const onClick = handleClickSuggestion;

      if (deleteAction) {
        onDelete = handleDelete;
        deleteIcon = (
          <span title={`Excluir ${suggestion.title}`}>
            <Cancel />
          </span>
        );
      }

      const { title } = suggestion;

      return (
        <ChipCustom
          label={suggestion.title}
          onClick={onClick}
          onDelete={onDelete}
          title={title}
          deleteIcon={deleteIcon}
        />
      );
    },
    [deleteAction, dispatch],
  );

  return (
    <Grid container justify="space-between">
      <Box
        display="flex"
        alignItems="center"
        flex={1}
        className={classes.suggestionsBox}
      >
        <Typography className={classes.title}>Sugestões</Typography>
        {suggestions.map((suggestion) => (
          <Box ml={1} key={suggestion.title}>
            {renderSuggestion(suggestion)}
          </Box>
        ))}
      </Box>
      <Box display="flex">
        {deleteAction ? (
          <IconButton onClick={() => setDeleteAction(false)}>
            <Close />
          </IconButton>
        ) : (
          <>
            <IconButton
              data-testid="delete-menu"
              aria-controls="suggestion-menu"
              aria-haspopup="true"
              onClick={handleClick}
            >
              <MoreVert htmlColor={iconColor} />
            </IconButton>
            <Menu
              id="suggestion-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem
                data-testid="delete-button-menu"
                onClick={handleCloseDelete}
              >
                Excluir Sugestão
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>
    </Grid>
  );
}
